import React from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle,
  MessageSquare,
  Home as HomeIcon,
  ShieldCheck,
  HardHat,
  Eye,
  HeartHandshake,
  Sparkles,
  Leaf,
  Phone,
  Sun,
  Battery,
  Thermometer,
  Droplet,
  Zap,
  Lightbulb,
  Info,
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';

const promises = [
  {
    icon: MessageSquare,
    title: 'Honest & Practical Advice',
    desc: 'We recommend solutions based on your property, lifestyle, business operations, energy usage and long-term goals — not simply the most expensive option.',
  },
  {
    icon: HomeIcon,
    title: 'Complete Energy Solutions',
    desc: 'Integrated solutions for homes and businesses across solar & battery, heat pump hot water, energy-efficient HVAC, EV charging, electrification and efficiency consulting.',
  },
  {
    icon: ShieldCheck,
    title: 'Quality Products & Trusted Technologies',
    desc: 'Carefully selected products and brands we trust for performance, reliability, efficiency and long-term value.',
  },
  {
    icon: HardHat,
    title: 'Professional Installation Standards',
    desc: 'Projects completed by experienced, qualified professionals who take pride in delivering safe, compliant and high-quality workmanship.',
  },
  {
    icon: Eye,
    title: 'Clear Communication & Transparency',
    desc: 'You should feel informed and supported throughout the entire process — from initial consultation through to installation and ongoing support.',
  },
  {
    icon: HeartHandshake,
    title: 'Long-Term Customer Support',
    desc: 'Our relationship continues well beyond installation day. We are here to support you throughout your full energy transition journey.',
  },
  {
    icon: Sparkles,
    title: 'Focused on Real Value',
    desc: 'True value comes from the right advice, quality workmanship, reliable support and solutions designed to genuinely benefit you long term.',
  },
  {
    icon: Leaf,
    title: 'Future-Focused Sustainable Living',
    desc: 'Our mission is to help Australian homes and businesses transition toward cleaner, smarter and more energy-efficient living.',
  },
];

const solutions = [
  { icon: Sun, label: 'Solar Systems' },
  { icon: Battery, label: 'Battery Storage' },
  { icon: Droplet, label: 'Heat Pump Hot Water' },
  { icon: Thermometer, label: 'Energy-Efficient HVAC' },
  { icon: Zap, label: 'EV Charging' },
  { icon: HomeIcon, label: 'Electrification' },
  { icon: Lightbulb, label: 'Efficiency Consulting' },
  { icon: ShieldCheck, label: 'Ongoing Support' },
];

const SupaPromisePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="The Supa Promise — Making Sustainable Living a Reality"
        description="Eight commitments to every Supa Energy Hub customer: honest advice, complete energy solutions, quality products, professional installation, transparent communication, long-term support, real value and sustainable living."
      />
      <Header />
      <main>
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#3D5A47] via-[#34503e] to-[#2d4435]">
          <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-[#B5D43B]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 lg:pt-32 lg:pb-28 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="text-white">
                <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-[0.25em] uppercase mb-4">
                  The Supa Promise
                </span>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mb-5">
                  Making Sustainable Living a{' '}
                  <span className="text-[#B5D43B]">Reality.</span>
                </h1>
                <p className="text-base sm:text-lg text-white/85 mb-4 max-w-xl leading-relaxed">
                  At Supa Energy Hub, we believe energy is more than just
                  electricity — it's about how homes and businesses live,
                  operate, save and transition toward a smarter, more
                  sustainable future.
                </p>
                <p className="text-base sm:text-lg text-white/85 mb-8 max-w-xl leading-relaxed">
                  When you choose Supa Energy Hub, you can expect more than
                  just products and installation. You get genuine support,
                  practical guidance and a long-term energy partner.
                </p>

                <div className="flex flex-wrap gap-3">
                  <Link to="/contact">
                    <Button
                      data-testid="promise-hero-cta"
                      className="bg-[#B5D43B] hover:bg-[#a0c133] text-[#3D5A47] gap-2 px-8 py-6 h-auto text-base font-semibold rounded-full"
                    >
                      Start the Conversation
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to="/services">
                    <Button
                      variant="outline"
                      data-testid="promise-hero-services"
                      className="border-white/30 bg-white/5 text-white hover:bg-white hover:text-[#3D5A47] gap-2 px-8 py-6 h-auto text-base font-semibold rounded-full backdrop-blur"
                    >
                      Explore Our Solutions
                      <Phone className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Hero seal */}
              <div className="relative flex justify-center lg:justify-end">
                <div
                  className="relative bg-white rounded-full shadow-2xl ring-1 ring-white/40 p-6 sm:p-8 w-full max-w-md aspect-square flex items-center justify-center"
                  data-testid="promise-hero-seal"
                >
                  <img
                    src="/promise-assets/supa-promise-logo.png"
                    alt="The Supa Promise — Our Promise. Your Confidence. A Better Energy Future Together."
                    className="block w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TAGLINE STRIP */}
        <section className="bg-[#B5D43B] py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-[#3D5A47] font-bold tracking-wider uppercase text-sm sm:text-base">
              Our Promise · Your Confidence · A Better Energy Future Together
            </p>
          </div>
        </section>

        {/* WHAT YOU CAN EXPECT */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                What You Can Expect From Us
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#3D5A47] mb-4">
                Eight commitments — every customer, every project.
              </h2>
              <p className="text-gray-600">
                The Supa Promise isn't a slogan. It's a set of standards we
                hold ourselves to from your first call all the way through to
                the years after your install.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {promises.map((p, i) => (
                <div
                  key={i}
                  data-testid={`promise-card-${i}`}
                  className="group bg-gray-50 hover:bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#B5D43B]/40 hover:shadow-xl hover:-translate-y-1 transition-all"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#3D5A47] text-[#B5D43B] flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                    <p.icon className="w-5 h-5" strokeWidth={2.4} />
                  </div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <CheckCircle className="w-4 h-4 text-[#B5D43B] flex-shrink-0" strokeWidth={2.5} />
                    <h3 className="font-bold text-[#3D5A47] text-base leading-tight">
                      {p.title}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {p.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* COMPLETE SOLUTIONS STRIP */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-5">
                <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                  Complete Energy Solutions
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#3D5A47] mb-5">
                  One trusted partner. Every step of your energy journey.
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Whether you're starting with rooftop solar, layering on a
                  battery, switching to heat-pump hot water, adding an EV
                  charger, or electrifying an entire home or business — we
                  cover the full stack.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  No juggling installers. No conflicting advice. Just one
                  long-term partner walking with you from first consult to
                  ongoing support.
                </p>
              </div>

              <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {solutions.map((s, i) => (
                  <div
                    key={i}
                    data-testid={`solution-${i}`}
                    className="group bg-white rounded-2xl p-4 border border-gray-100 hover:border-[#B5D43B]/40 hover:shadow-lg transition-all flex flex-col items-center text-center"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 text-[#3D5A47] flex items-center justify-center mb-3 group-hover:bg-[#B5D43B] transition-colors">
                      <s.icon className="w-5 h-5" strokeWidth={2.2} />
                    </div>
                    <span className="text-xs font-semibold text-[#3D5A47] leading-tight">
                      {s.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* MORE THAN AN INSTALLER */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 bg-[#B5D43B]/15 border border-[#B5D43B]/40 rounded-full px-4 py-1.5 mb-6">
              <Sparkles className="w-4 h-4 text-[#3D5A47]" strokeWidth={2.5} />
              <span className="text-xs font-semibold tracking-wider uppercase text-[#3D5A47]">
                More Than Just an Installer
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#3D5A47] mb-5">
              A trusted long-term energy partner.
            </h2>
            <p className="text-gray-600 leading-relaxed text-base sm:text-lg">
              We aim to be a partner our customers stay with — helping them
              navigate electrification, energy efficiency and sustainable
              living with confidence. The Supa Promise is how we keep showing
              up, year after year, install after install.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#3D5A47]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to make sustainable living a reality?
            </h2>
            <p className="text-white/85 mb-8 max-w-2xl mx-auto leading-relaxed">
              Talk to a Supa Energy Hub specialist for honest advice on what
              fits your home, your business and your goals — no pressure, no
              jargon.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/get-estimate">
                <Button
                  data-testid="promise-cta-estimate"
                  className="bg-[#B5D43B] hover:bg-[#a0c133] text-[#3D5A47] gap-2 px-8 py-6 h-auto text-base font-semibold rounded-full"
                >
                  Get a Free Estimate
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/supa-value-guarantee">
                <Button
                  variant="outline"
                  data-testid="promise-cta-svg"
                  className="border-white/30 bg-white/5 text-white hover:bg-white hover:text-[#3D5A47] gap-2 px-8 py-6 h-auto text-base font-semibold rounded-full backdrop-blur"
                >
                  Submit a Quote to Beat
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* DISCLAIMERS */}
        <section
          id="disclaimers"
          className="py-14 bg-white border-t border-gray-100"
          data-testid="promise-disclaimers"
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-5 h-5 text-[#3D5A47]" strokeWidth={2.4} />
                <h2 className="text-lg sm:text-xl font-bold text-[#3D5A47]">
                  Important Information
                </h2>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed mb-4">
                The Supa Promise sets out the standards Supa Energy Hub aims
                to deliver across every customer engagement. It is a
                statement of our service ethos and is not a contract, a
                product warranty, a financial product, or a guarantee of any
                specific outcome, saving, performance, timeframe, rebate
                value or commercial result.
              </p>
              <ol className="list-decimal pl-5 space-y-2 text-xs text-gray-600 leading-relaxed">
                <li>
                  <strong>General information only.</strong> Content on this
                  page is general in nature and does not take into account
                  your individual objectives, financial situation, energy
                  usage or property characteristics. Please obtain
                  independent advice as appropriate before making any
                  purchasing or investment decision.
                </li>
                <li>
                  <strong>Products &amp; technologies.</strong> Specific
                  products, brands and technologies recommended by Supa
                  Energy Hub are selected at our discretion based on our
                  current view of performance, reliability, efficiency and
                  long-term value. Availability, pricing and specifications
                  may change without notice. All third-party trademarks
                  remain the property of their respective owners.
                </li>
                <li>
                  <strong>Installation &amp; workmanship.</strong> Our
                  installations are performed by qualified, licensed and
                  accredited tradespeople under applicable Australian
                  Standards and the rules of relevant industry bodies
                  (including, where applicable, the Clean Energy Council).
                  Specific warranty terms for products and workmanship are
                  set out in the documents provided with your individual
                  quote and installation.
                </li>
                <li>
                  <strong>Support claims.</strong> Statements such as
                  "long-term customer support" describe Supa Energy Hub's
                  commitment to be reachable for service, warranty and
                  guidance after installation. They do not extend or
                  override the specific warranty period of any product or
                  workmanship guarantee, which prevail in the event of
                  inconsistency.
                </li>
                <li>
                  <strong>Sustainability &amp; "future-focused" claims.</strong>{' '}
                  References to "sustainable living", "cleaner energy
                  future", "electrification" and similar are aspirational
                  statements of intent. Actual environmental impact depends
                  on the specific products installed, your usage profile,
                  the prevailing grid generation mix and other factors
                  beyond our control.
                </li>
                <li>
                  <strong>Logos &amp; trademarks.</strong> "The Supa
                  Promise" name and seal, "Supa Energy Hub" and the Supa
                  Energy Hub logo are trademarks of Supa Energy Hub and are
                  used here under our own authority. Other names, logos and
                  brand assets are the property of their respective owners.
                </li>
                <li>
                  <strong>No professional advice.</strong> Nothing on this
                  page constitutes energy, financial, taxation, legal or
                  professional advice. For binding pricing, specifications
                  and warranty terms, please request a written, site-specific
                  quote.
                </li>
              </ol>
              <p className="text-xs text-gray-500 leading-relaxed mt-5 border-t border-gray-200 pt-4">
                Supa Energy Hub complies with the Australian Consumer Law
                (Schedule 2 of the Competition and Consumer Act 2010 (Cth)).
                Nothing on this page is intended to mislead or deceive, or
                to exclude, restrict or modify any consumer guarantee or
                right that cannot lawfully be excluded.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default SupaPromisePage;
